import { useEffect, useMemo, useState } from 'react'
import {
  Badge,
  BadgeStatus,
  Button,
  Card,
  CardHeader,
  Checkbox,
  ConfirmDialog,
  DataTable,
  Input,
  KolomForm,
  Modal,
  PageHeader,
  Select,
  Tabs,
  Textarea,
  useToast,
  type ItemTab,
  type KolomTabel,
} from '@/components'
import { api, pesanKesalahan } from '@/lib/api'
import { formatTanggalJam } from '@/lib/format'
import type { Izin, Pengguna, Peran } from '@/lib/types'
import { useApi, usePaged } from '@/lib/useApi'
import { useOpsiUnit } from '@/lib/opsi'
import { useAuth } from '@/lib/auth'

const SKOP = ['NONE', 'READ', 'WRITE', 'APPROVE', 'FULL']

export default function PenggunaHakAkses() {
  const [tab, setTab] = useState('pengguna')
  const { punyaIzin } = useAuth()

  const tabDaftar: ItemTab[] = [
    { id: 'pengguna', label: 'Pengguna', izin: ['user:read'] },
    { id: 'peran', label: 'Peran', izin: ['role:read'] },
    { id: 'matriks', label: 'Matriks Peran & Izin', izin: ['role:read', 'user:read'] },
  ]

  return (
    <div>
      <PageHeader
        judul="Pengguna dan Hak Akses"
        deskripsi="Kelola akun, peran, penugasan unit beserta tingkat aksesnya, serta matriks izin per peran."
      />

      <Tabs tab={tabDaftar} aktif={tab} onUbah={setTab} />

      {tab === 'pengguna' ? <PanelPengguna punyaIzin={punyaIzin} /> : null}
      {tab === 'peran' ? <PanelPeran /> : null}
      {tab === 'matriks' ? <PanelMatriks /> : null}
    </div>
  )
}

function PanelPengguna({ punyaIzin }: { punyaIzin: (...kode: string[]) => boolean }) {
  const toast = useToast()
  const opsiUnit = useOpsiUnit()
  const daftar = usePaged<Pengguna>('/users', { perHalaman: 50 })
  const peran = useApi<unknown>('/roles', { perPage: 100 })
  const daftarPeran: Peran[] = Array.isArray(peran.data)
    ? (peran.data as Peran[])
    : ((peran.data as { items?: Peran[] } | null)?.items ?? [])

  const [modalPeran, setModalPeran] = useState<Pengguna | null>(null)
  const [modalUnit, setModalUnit] = useState<Pengguna | null>(null)
  const [konfirmasi, setKonfirmasi] = useState<{ jenis: 'reset' | 'lock' | 'unlock'; data: Pengguna } | null>(null)

  const [kodePeran, setKodePeran] = useState<Record<string, boolean>>({})
  const [penugasan, setPenugasan] = useState<Array<{ unitId: string; accessScope: string }>>([])
  const [simpanMemuat, setSimpanMemuat] = useState(false)

  const kolom: KolomTabel<Pengguna>[] = [
    {
      kunci: 'fullName',
      judul: 'Nama',
      render: (b) => (
        <div>
          <p className="font-medium text-netral-800">{b.fullName}</p>
          <p className="text-xs text-netral-500">@{b.username}</p>
        </div>
      ),
    },
    { kunci: 'email', judul: 'Email', render: (b) => b.email ?? '—' },
    {
      kunci: 'roles',
      judul: 'Peran',
      render: (b) => {
        const r = b.userRoles?.map((x) => x.role.code) ?? b.roles?.map((x) => x.code) ?? []
        return r.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {r.map((k) => (
              <Badge key={k} nada="info">
                {k}
              </Badge>
            ))}
          </div>
        ) : (
          <span className="text-xs text-netral-400">Tanpa peran</span>
        )
      },
    },
    {
      kunci: 'units',
      judul: 'Penugasan Unit',
      render: (b) => {
        const u = b.unitAssignments ?? []
        if (u.length === 0) return <span className="text-xs text-netral-400">Belum ditugaskan</span>
        return (
          <div className="space-y-0.5">
            {u.map((a) => (
              <p key={a.id ?? a.unitId} className="text-xs text-netral-600">
                {a.unit?.name ?? a.unitId} — <span className="font-medium">{a.accessScope}</span>
                {a.isDefault ? ' (utama)' : ''}
              </p>
            ))}
          </div>
        )
      },
    },
    { kunci: 'status', judul: 'Status', render: (b) => <BadgeStatus status={b.status} /> },
    {
      kunci: 'lastLoginAt',
      judul: 'Login Terakhir',
      render: (b) => (b.lastLoginAt ? formatTanggalJam(b.lastLoginAt) : '—'),
    },
    {
      kunci: 'aksi',
      judul: 'Aksi',
      sortable: false,
      render: (b) => (
        <div className="flex flex-wrap gap-1.5">
          {punyaIzin('user:manage') ? (
            <>
              <Button
                ukuran="kecil"
                varian="garis"
                onClick={() => {
                  const awal: Record<string, boolean> = {}
                  for (const r of b.userRoles?.map((x) => x.role.code) ?? b.roles?.map((x) => x.code) ?? []) {
                    awal[r] = true
                  }
                  setKodePeran(awal)
                  setModalPeran(b)
                }}
              >
                Peran
              </Button>
              <Button
                ukuran="kecil"
                varian="garis"
                onClick={() => {
                  setPenugasan(
                    (b.unitAssignments ?? []).map((a) => ({ unitId: a.unitId, accessScope: a.accessScope })),
                  )
                  setModalUnit(b)
                }}
              >
                Unit
              </Button>
              <Button ukuran="kecil" varian="halus" onClick={() => setKonfirmasi({ jenis: 'reset', data: b })}>
                Reset Sandi
              </Button>
              {b.lockedUntil ? (
                <Button ukuran="kecil" varian="utama" onClick={() => setKonfirmasi({ jenis: 'unlock', data: b })}>
                  Buka Kunci
                </Button>
              ) : (
                <Button ukuran="kecil" varian="bahaya" onClick={() => setKonfirmasi({ jenis: 'lock', data: b })}>
                  Kunci
                </Button>
              )}
            </>
          ) : (
            <span className="text-xs text-netral-400">Tanpa hak kelola</span>
          )}
        </div>
      ),
    },
  ]

  const simpanPeran = async () => {
    if (!modalPeran) return
    const idPeran = daftarPeran.filter((r) => kodePeran[r.code]).map((r) => r.id)
    setSimpanMemuat(true)
    try {
      await api.put(`/users/${modalPeran.id}/roles`, { roleIds: idPeran })
      toast.sukses('Peran pengguna diperbarui.')
      setModalPeran(null)
      daftar.muat()
    } catch (e) {
      toast.gagal(pesanKesalahan(e))
    } finally {
      setSimpanMemuat(false)
    }
  }

  const simpanUnit = async () => {
    if (!modalUnit) return
    setSimpanMemuat(true)
    try {
      await api.put(`/users/${modalUnit.id}/units`, { units: penugasan.filter((p) => p.unitId) })
      toast.sukses('Penugasan unit diperbarui.')
      setModalUnit(null)
      daftar.muat()
    } catch (e) {
      toast.gagal(pesanKesalahan(e))
    } finally {
      setSimpanMemuat(false)
    }
  }

  return (
    <>
      <DataTable
        kolom={kolom}
        data={daftar.items}
        kunciBaris={(b) => b.id}
        memuat={daftar.memuat}
        galat={daftar.galat}
        kodeGalat={daftar.kodeGalat}
        onCoba={daftar.muat}
        meta={daftar.meta}
        onHalaman={daftar.setHalaman}
        pesanKosong="Belum ada pengguna."
      />

      <Modal
        terbuka={modalPeran !== null}
        onTutup={() => setModalPeran(null)}
        judul={modalPeran ? `Peran — ${modalPeran.fullName}` : 'Peran'}
        lebar="max-w-xl"
        footer={
          <>
            <Button varian="garis" onClick={() => setModalPeran(null)} disabled={simpanMemuat}>
              Batal
            </Button>
            <Button onClick={() => void simpanPeran()} memuat={simpanMemuat}>
              Simpan Peran
            </Button>
          </>
        }
      >
        <div className="space-y-2">
          {peran.galat ? <p className="text-sm text-red-700">Gagal memuat peran: {peran.galat}</p> : null}
          {daftarPeran.map((r) => (
            <Checkbox
              key={r.id}
              label={`${r.name} (${r.code})`}
              checked={Boolean(kodePeran[r.code])}
              onChange={(e) => setKodePeran((k) => ({ ...k, [r.code]: e.target.checked }))}
            />
          ))}
        </div>
      </Modal>

      <Modal
        terbuka={modalUnit !== null}
        onTutup={() => setModalUnit(null)}
        judul={modalUnit ? `Penugasan Unit — ${modalUnit.fullName}` : 'Penugasan Unit'}
        deskripsi="Scope unit berasal dari penugasan ini, bukan dari klaim klien."
        lebar="max-w-2xl"
        footer={
          <>
            <Button varian="garis" onClick={() => setModalUnit(null)} disabled={simpanMemuat}>
              Batal
            </Button>
            <Button onClick={() => void simpanUnit()} memuat={simpanMemuat}>
              Simpan Penugasan
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Button
            ukuran="kecil"
            varian="garis"
            onClick={() =>
              setPenugasan((p) => [...p, { unitId: '', accessScope: 'READ' }])
            }
          >
            + Tambah Unit
          </Button>
          {penugasan.map((p, i) => (
            <div key={i} className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_160px_auto]">
              <Select
                placeholder="Pilih unit"
                opsi={opsiUnit}
                value={p.unitId}
                onChange={(e) =>
                  setPenugasan((arr) => arr.map((x, j) => (j === i ? { ...x, unitId: e.target.value } : x)))
                }
              />
              <Select
                opsi={SKOP.map((s) => ({ value: s, label: s }))}
                value={p.accessScope}
                onChange={(e) =>
                  setPenugasan((arr) => arr.map((x, j) => (j === i ? { ...x, accessScope: e.target.value } : x)))
                }
              />
              <Button varian="halus" ukuran="kecil" onClick={() => setPenugasan((arr) => arr.filter((_, j) => j !== i))}>
                Hapus
              </Button>
            </div>
          ))}
          {penugasan.length === 0 ? (
            <p className="text-sm text-netral-500">Belum ada penugasan unit untuk pengguna ini.</p>
          ) : null}
        </div>
      </Modal>

      <ConfirmDialog
        terbuka={konfirmasi !== null}
        onTutup={() => setKonfirmasi(null)}
        judul={
          konfirmasi?.jenis === 'reset'
            ? 'Reset Kata Sandi'
            : konfirmasi?.jenis === 'lock'
              ? 'Kunci Akun'
              : 'Buka Kunci Akun'
        }
        pesan={
          konfirmasi?.jenis === 'reset'
            ? `Kata sandi ${konfirmasi?.data.fullName} akan direset dan pengguna WAJIB menggantinya saat masuk berikutnya.`
            : `Akun ${konfirmasi?.data.fullName} akan ${konfirmasi?.jenis === 'lock' ? 'dikunci' : 'dibuka kuncinya'}.`
        }
        labelKonfirmasi="Lanjutkan"
        berbahaya={konfirmasi?.jenis !== 'unlock'}
        memuat={false}
        onKonfirmasi={async () => {
          if (!konfirmasi) return
          const rute =
            konfirmasi.jenis === 'reset'
              ? `/users/${konfirmasi.data.id}/reset-password`
              : `/users/${konfirmasi.data.id}/${konfirmasi.jenis}`
          try {
            const hasil = await api.post<{ kataSandiSementara?: string }>(rute, {})
            toast.sukses(
              hasil?.kataSandiSementara
                ? `Kata sandi sementara: ${hasil.kataSandiSementara}`
                : 'Tindakan berhasil dijalankan.',
            )
            setKonfirmasi(null)
            daftar.muat()
          } catch (e) {
            toast.gagal(pesanKesalahan(e))
          }
        }}
      />
    </>
  )
}

function PanelPeran() {
  const toast = useToast()
  const daftar = usePaged<Peran>('/roles', { perHalaman: 100 })
  const [modal, setModal] = useState(false)
  const [nama, setNama] = useState('')
  const [kode, setKode] = useState('')
  const [deskripsi, setDeskripsi] = useState('')
  const [galat, setGalat] = useState<string | null>(null)
  const [memuat, setMemuat] = useState(false)

  const kolom: KolomTabel<Peran>[] = [
    { kunci: 'code', judul: 'Kode', render: (b) => <span className="font-mono text-xs">{b.code}</span> },
    { kunci: 'name', judul: 'Nama Peran' },
    { kunci: 'description', judul: 'Keterangan', render: (b) => b.description ?? '—' },
    {
      kunci: 'isSystem',
      judul: 'Sistem',
      render: (b) => (b.isSystem ? <Badge nada="peringatan">Peran sistem</Badge> : <Badge nada="netral">Kustom</Badge>),
    },
  ]

  const simpan = async () => {
    setGalat(null)
    if (!nama.trim() || !kode.trim()) return setGalat('Kode dan nama peran wajib diisi.')
    setMemuat(true)
    try {
      await api.post('/roles', { code: kode.trim().toLowerCase(), name: nama.trim(), description: deskripsi.trim() || undefined })
      toast.sukses('Peran berhasil dibuat.')
      setModal(false)
      setNama('')
      setKode('')
      setDeskripsi('')
      daftar.muat()
    } catch (e) {
      setGalat(pesanKesalahan(e))
    } finally {
      setMemuat(false)
    }
  }

  return (
    <>
      <Card padat className="mb-3 overflow-hidden">
        <div className="flex items-center justify-between border-b border-netral-200 px-4 py-2.5">
          <CardHeader judul="Daftar Peran" deskripsi="Peran menentukan himpunan izin yang melekat pada pengguna." />
          <Button ukuran="kecil" onClick={() => setModal(true)}>
            + Peran Baru
          </Button>
        </div>
        <DataTable
          kolom={kolom}
          data={daftar.items}
          kunciBaris={(b) => b.id}
          memuat={daftar.memuat}
          galat={daftar.galat}
          kodeGalat={daftar.kodeGalat}
          onCoba={daftar.muat}
          pesanKosong="Belum ada peran."
        />
      </Card>

      <Modal
        terbuka={modal}
        onTutup={() => setModal(false)}
        judul="Peran Baru"
        lebar="max-w-xl"
        footer={
          <>
            <Button varian="garis" onClick={() => setModal(false)} disabled={memuat}>
              Batal
            </Button>
            <Button onClick={() => void simpan()} memuat={memuat}>
              Simpan Peran
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {galat ? (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {galat}
            </div>
          ) : null}
          <KolomForm kolom={2}>
            <Input label="Kode Peran" value={kode} onChange={(e) => setKode(e.target.value)} required bantuan="Huruf kecil, tanpa spasi." />
            <Input label="Nama Peran" value={nama} onChange={(e) => setNama(e.target.value)} required />
          </KolomForm>
          <Textarea label="Keterangan" value={deskripsi} onChange={(e) => setDeskripsi(e.target.value)} rows={2} />
        </div>
      </Modal>
    </>
  )
}

function PanelMatriks() {
  const toast = useToast()
  const [peranId, setPeranId] = useState('')
  const izin = useApi<unknown>('/permissions', {})
  const daftarIzin: Izin[] = useMemo(() => {
    const d = izin.data
    if (Array.isArray(d)) return d as Izin[]
    return ((d as { items?: Izin[] } | null)?.items ?? []) as Izin[]
  }, [izin.data])

  const peran = useApi<unknown>('/roles', { perPage: 100 })
  const daftarPeran: Peran[] = Array.isArray(peran.data)
    ? (peran.data as Peran[])
    : ((peran.data as { items?: Peran[] } | null)?.items ?? [])

  const [terpilihIzin, setTerpilihIzin] = useState<Set<string>>(new Set())

  // Saat peran berganti, muat izin yang dimiliki peran tersebut (bila API menyertakannya).
  useEffect(() => {
    const p = daftarPeran.find((r) => r.id === peranId)
    const kode = p?.permissions ?? p?.rolePermissions?.map((rp) => rp.permissionId) ?? []
    setTerpilihIzin(new Set(kode))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peranId, peran.data])

  const modul = useMemo(() => {
    const peta = new Map<string, Izin[]>()
    for (const i of daftarIzin) {
      const m = i.module || (i.code.includes(':') ? i.code.split(':')[0] : 'lainnya')
      const arr = peta.get(m) ?? []
      arr.push(i)
      peta.set(m, arr)
    }
    return [...peta.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [daftarIzin])

  return (
    <Card padat className="overflow-hidden">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-netral-200 px-4 py-3">
        <CardHeader
          judul="Matriks Peran & Izin"
          deskripsi="Katalog izin resmi dari GET /permissions, dikelompokkan per modul. Kode izin sama persis dengan backend."
        />
        <div className="min-w-56">
          <Select
            label="Peran untuk membandingkan"
            placeholder="Pilih peran"
            opsi={daftarPeran.map((r) => ({ value: r.id, label: `${r.name} (${r.code})` }))}
            value={peranId}
            onChange={(e) => setPeranId(e.target.value)}
          />
        </div>
      </div>

      {izin.galat ? <p className="px-4 py-3 text-sm text-red-700">Gagal memuat izin: {izin.galat}</p> : null}
      {izin.memuat ? <p className="px-4 py-3 text-sm text-netral-500">Memuat katalog izin…</p> : null}

      <div className="max-h-[70vh] divide-y divide-netral-100 overflow-y-auto">
        {modul.map(([nama, daftar]) => (
          <div key={nama} className="px-4 py-3">
            <div className="mb-2 flex items-center gap-2">
              <h3 className="text-sm font-semibold text-netral-800">{nama}</h3>
              <Badge nada="netral">{daftar.length} izin</Badge>
            </div>
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
              {daftar.map((i) => (
                <label key={i.code} className="flex items-start gap-2 rounded-lg border border-netral-200 px-2.5 py-1.5">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-netral-300"
                    checked={terpilihIzin.has(i.code)}
                    disabled={!peranId}
                    onChange={async (e) => {
                      if (!peranId) {
                        toast.info('Pilih peran terlebih dahulu untuk mengubah izin.')
                        return
                      }
                      const baru = new Set(terpilihIzin)
                      if (e.target.checked) baru.add(i.code)
                      else baru.delete(i.code)
                      setTerpilihIzin(baru)
                      try {
                        await api.put(`/roles/${peranId}/permissions`, { permissions: [...baru] })
                        toast.sukses('Izin peran diperbarui.')
                        peran.muat()
                      } catch (err) {
                        toast.gagal(pesanKesalahan(err))
                      }
                    }}
                  />
                  <span className="min-w-0">
                    <span className="block font-mono text-xs text-netral-800">{i.code}</span>
                    {i.description ? (
                      <span className="block text-xs text-netral-500">{i.description}</span>
                    ) : null}
                  </span>
                </label>
              ))}
            </div>
          </div>
        ))}
        {!izin.memuat && modul.length === 0 && !izin.galat ? (
          <p className="px-4 py-3 text-sm text-netral-500">Katalog izin kosong.</p>
        ) : null}
      </div>
    </Card>
  )
}
